import React, { useEffect, useRef, useState } from 'react';
import { OFFICIAL_LINKS, INQUIRY_SOLUTIONS } from '../data/servicesData';
import { Forms } from '../../api/requests/Forms';
import { CheckCircle, Send, Phone, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const EMPTY_FORM = { fullName: '', mobile: '', email: '', companyName: '', city: '', message: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const inputCls = (hasError, large) =>
  `w-full px-3.5 ${large ? 'py-3' : 'py-2.5'} rounded-xl border text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-colors ${
    hasError
      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
      : 'border-slate-300 focus:border-blue-700 focus:ring-blue-100'
  }`;

const Label = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="block text-xs font-bold text-slate-800 mb-1">
    {children}{' '}
    {required ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-medium">(optional)</span>}
  </label>
);

const FieldError = ({ children }) => <p className="text-[11px] text-red-600 mt-1 font-medium">{children}</p>;

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between gap-3">
    <span className="text-slate-400 shrink-0">{label}</span>
    <span className={`font-bold text-right truncate ${highlight ? 'text-blue-700' : 'text-slate-800'}`}>{value}</span>
  </div>
);

/**
 * Enquiry form used by the home-page popup and the /apply ad page.
 * `meta` is merged into the stored form_meta (e.g. UTM / ad click ids).
 * Remount (change `key`) to reset it.
 */
export const InquiryForm = ({
  preselectedSolution = '',
  source = 'website',
  meta,
  onSubmitted,
  onDone,
  doneLabel = 'Back to website',
  autoFocus = true,
  large = false,
  showMessage = true,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [solution, setSolution] = useState(preselectedSolution || INQUIRY_SOLUTIONS[0]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (preselectedSolution) setSolution(preselectedSolution);
  }, [preselectedSolution]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [autoFocus]);

  const solutionOptions = INQUIRY_SOLUTIONS.includes(solution) ? INQUIRY_SOLUTIONS : [solution, ...INQUIRY_SOLUTIONS];

  const update = (field) => (e) => {
    const value = field === 'mobile' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((errs) => ({ ...errs, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (form.fullName.trim().length < 2) errs.fullName = 'Please enter your full name';
    if (!/^[6-9]\d{9}$/.test(form.mobile)) errs.mobile = 'Enter a valid 10-digit Indian mobile number';
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) errs.email = 'Enter a valid email address';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await Forms.submitWebsiteInquiry({ ...form, solution, source, meta });
      const result = {
        ticketId: res?.data?.form_meta?.ticket_id || '',
        fullName: form.fullName.trim(),
        companyName: form.companyName.trim(),
        mobile: form.mobile,
        solution,
      };
      setSubmitted(result);
      onSubmitted && onSubmitted(result);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          'We could not submit your request right now. Please try again or reach us on WhatsApp.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const d = submitted || { ...form, solution };
    const lines = [
      'Hello Fi Pay Bills Team,',
      d.ticketId ? `Reference: ${d.ticketId}` : null,
      d.fullName ? `Name: ${d.fullName}` : null,
      d.companyName ? `Company: ${d.companyName}` : null,
      `Solution: ${d.solution}`,
      d.mobile ? `Mobile: ${d.mobile}` : null,
    ].filter(Boolean);
    window.open(`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  if (submitted) {
    return (
      <div className="text-center py-4 space-y-4">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10" />
        </div>

        <div>
          <h4 className="text-lg font-black text-slate-900">Thank you, {submitted.fullName.split(' ')[0]}!</h4>
          <p className="text-xs text-slate-600 mt-1">Our team will call you back within one working day.</p>
          {submitted.ticketId && (
            <>
              <p className="text-xs text-slate-500 mt-3">Your reference number</p>
              <div className="inline-block mt-1.5 px-4 py-1.5 rounded-xl bg-blue-50 text-blue-900 font-mono font-black text-base border border-blue-200">
                {submitted.ticketId}
              </div>
            </>
          )}
        </div>

        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-600 text-left space-y-1.5">
          <Row label="Name" value={submitted.fullName} />
          {submitted.companyName && <Row label="Company" value={submitted.companyName} />}
          <Row label="Mobile" value={`+91 ${submitted.mobile}`} />
          <Row label="Solution" value={submitted.solution} highlight />
        </div>

        <div className="space-y-2 pt-1">
          <button
            onClick={openWhatsApp}
            className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm flex items-center justify-center gap-2 cursor-pointer"
          >
            <Phone className="w-4 h-4" />
            <span>Need it faster? Chat on WhatsApp</span>
          </button>
          {onDone && (
            <button
              onClick={onDone}
              className="w-full py-2.5 px-4 rounded-xl text-slate-600 hover:bg-slate-100 text-xs font-semibold transition-colors cursor-pointer"
            >
              {doneLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <Label htmlFor="inq-name" required>Full name</Label>
        <input
          id="inq-name"
          ref={firstFieldRef}
          type="text"
          autoComplete="name"
          value={form.fullName}
          onChange={update('fullName')}
          placeholder="Your full name"
          className={inputCls(errors.fullName, large)}
        />
        {errors.fullName && <FieldError>{errors.fullName}</FieldError>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="inq-mobile" required>Mobile number</Label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">+91</span>
            <input
              id="inq-mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              value={form.mobile}
              onChange={update('mobile')}
              placeholder="10-digit number"
              className={`${inputCls(errors.mobile, large)} pl-12 font-mono`}
            />
          </div>
          {errors.mobile && <FieldError>{errors.mobile}</FieldError>}
        </div>
        <div>
          <Label htmlFor="inq-email">Email</Label>
          <input
            id="inq-email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={update('email')}
            placeholder="you@company.com"
            className={inputCls(errors.email, large)}
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="inq-company">Company / shop name</Label>
          <input
            id="inq-company"
            type="text"
            autoComplete="organization"
            value={form.companyName}
            onChange={update('companyName')}
            placeholder="e.g. Sri Balaji Enterprises"
            className={inputCls(false, large)}
          />
        </div>
        <div>
          <Label htmlFor="inq-city">City / pincode</Label>
          <input
            id="inq-city"
            type="text"
            autoComplete="address-level2"
            value={form.city}
            onChange={update('city')}
            placeholder="e.g. Hyderabad / 500081"
            className={inputCls(false, large)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="inq-solution" required>I'm interested in</Label>
        <select
          id="inq-solution"
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
          className={inputCls(false, large)}
        >
          {solutionOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {showMessage && (
        <div>
          <Label htmlFor="inq-message">Anything else we should know?</Label>
          <textarea
            id="inq-message"
            rows={2}
            maxLength={1000}
            value={form.message}
            onChange={update('message')}
            placeholder="Monthly volume, timeline, questions…"
            className={`${inputCls(false, large)} resize-none`}
          />
        </div>
      )}

      {submitError && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p>{submitError}</p>
            <button type="button" onClick={openWhatsApp} className="font-bold underline cursor-pointer">
              Send on WhatsApp instead
            </button>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={`w-full ${large ? 'py-4 text-sm' : 'py-3.5 text-xs'} rounded-2xl bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 disabled:opacity-70 disabled:cursor-wait text-white font-black uppercase tracking-wider transition-all shadow-lg shadow-blue-700/25 flex items-center justify-center gap-2 cursor-pointer`}
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        <span>{submitting ? 'Submitting…' : 'Submit Request'}</span>
      </button>

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 text-center">
        <ShieldCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
        <span>Your details are only used to contact you about this request. No spam.</span>
      </div>
    </form>
  );
};
